import { FileText } from "lucide-react";
import type { ReactNode } from "react";

import { BrandArrow } from "@/components/brand-arrow";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type { WeeklyStatus } from "@/lib/api";
import { ragBadgeClass, ragLabel } from "@/lib/rag";
import { cn } from "@/lib/utils";

/** The fixed set of lifecycle states a weekly status can be in. */
type StatusState = "draft" | "in_review" | "published";

const STATE_LABEL: Record<StatusState, string> = {
  draft: "Draft",
  in_review: "In review",
  published: "Published",
};

const STATE_VARIANT: Record<StatusState, "outline" | "secondary" | "default"> = {
  draft: "outline",
  in_review: "secondary",
  published: "default",
};

/** Coerce the API `state` string into the constrained lifecycle union. */
function normalizeState(value: string): StatusState {
  return value === "in_review" || value === "published" ? value : "draft";
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface StatusFieldProps {
  label: string;
  value: string | null | undefined;
  /** Render as a BrandArrow-bulleted action list (used for next steps). */
  asActions?: boolean;
}

function StatusField({ label, value, asActions }: StatusFieldProps) {
  if (!value) {
    return null;
  }
  const items = asActions
    ? value
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
    : [];
  return (
    <div>
      <p className="text-muted-foreground text-xs font-medium uppercase">{label}</p>
      {asActions && items.length > 0 ? (
        <ul className="mt-1 space-y-1">
          {items.map((item, i) => (
            <li key={i} className="text-foreground flex items-start gap-2 text-sm">
              <BrandArrow className="text-primary mt-1 shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-foreground mt-1 text-sm whitespace-pre-wrap">{value}</p>
      )}
    </div>
  );
}

interface StatusTimelineProps {
  statuses: WeeklyStatus[];
  renderActions?: (status: WeeklyStatus) => ReactNode;
}

export function StatusTimeline({ statuses, renderActions }: StatusTimelineProps) {
  if (statuses.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <p className="text-muted-foreground text-sm">No status updates yet.</p>
        </CardContent>
      </Card>
    );
  }

  const ordered = [...statuses].sort(
    (a, b) => new Date(b.week_start).getTime() - new Date(a.week_start).getTime(),
  );

  return (
    <div className="space-y-4">
      {ordered.map((status) => {
        const actions = renderActions?.(status);
        const state = normalizeState(status.state);
        return (
          <Card key={status.id}>
            <CardContent className="space-y-4 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-foreground font-medium">
                    Week of {formatDate(status.week_start)}
                  </span>
                  <Badge variant={STATE_VARIANT[state]}>{STATE_LABEL[state]}</Badge>
                  <span className={cn(ragBadgeClass(status.rag))}>{ragLabel(status.rag)}</span>
                </div>
                <span className="text-muted-foreground text-sm tabular-nums">
                  {status.percent_complete}% complete
                </span>
              </div>

              <Progress value={status.percent_complete} />

              <div className="space-y-3">
                <StatusField label="Summary" value={status.summary} />
                <StatusField label="Progress" value={status.progress} />
                <StatusField label="Blockers" value={status.blockers} />
                <StatusField label="Next steps" value={status.next_steps} asActions />
              </div>

              {status.source === "granola" && status.source_refs.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-muted-foreground text-xs">From Granola:</span>
                  {status.source_refs.map((ref, i) => (
                    <Badge key={ref.meeting_id ?? i} variant="outline" className="gap-1">
                      <FileText className="h-3 w-3" />
                      {ref.title ?? ref.meeting_id ?? "Meeting"}
                    </Badge>
                  ))}
                </div>
              )}

              {actions && (
                <div className="border-border flex flex-wrap justify-end gap-2 border-t pt-3">
                  {actions}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
